import type {
  InitGameOptions,
  YachtTurnModeConfig,
  YachtViewerState,
} from "@cloudnest/yacht-engine";
import { useSyncExternalStore } from "react";
import { createGameApi } from "../../shared/services/game-api";
import { getCurrentUserId } from "../../shared/store/auth-store";

const DEFAULT_GAME_TYPE = "yacht";
let gameApi = createGameApi(DEFAULT_GAME_TYPE);

export interface YachtMultiplayerState {
  readonly viewerState: YachtViewerState | null;
  readonly lastError: { readonly error: string } | null;
  readonly connection:
    | "idle"
    | "connecting"
    | "open"
    | "reconnecting"
    | "closed";
  readonly lastAppliedSeq: number;
  readonly roomId: string | null;
  readonly gameType: string;
  readonly myPlayerId: string | null;
  readonly isHost: boolean;
  readonly isObserver: boolean;
}

interface ViewerStateRow {
  readonly sequenceNumber: number;
  readonly state: Record<string, unknown>;
}

const INITIAL_STATE: YachtMultiplayerState = {
  viewerState: null,
  lastError: null,
  connection: "idle",
  lastAppliedSeq: 0,
  roomId: null,
  gameType: DEFAULT_GAME_TYPE,
  myPlayerId: null,
  isHost: false,
  isObserver: false,
};

let state: YachtMultiplayerState = { ...INITIAL_STATE };
const listeners = new Set<() => void>();

function notify(): void {
  for (const listener of listeners) listener();
}

function wait(ms: number): Promise<void> {
  return new Promise((resolve) => {
    globalThis.setTimeout(resolve, ms);
  });
}

function setError(error: unknown, fallback: string): void {
  state = {
    ...state,
    lastError: {
      error: error instanceof Error ? error.message : fallback,
    },
  };
  notify();
}

function adoptLatest(rows: readonly ViewerStateRow[], fromSeq: number): number {
  let bestSeq = fromSeq;
  let best: Record<string, unknown> | null = null;
  for (const row of rows) {
    if (row.sequenceNumber > bestSeq) {
      bestSeq = row.sequenceNumber;
      best = row.state;
    }
  }

  if (best !== null) {
    state = {
      ...state,
      viewerState: best as unknown as YachtViewerState,
      lastAppliedSeq: bestSeq,
    };
  }

  return bestSeq;
}

async function adoptProjectedViewerState(
  roomId: string,
  fromSeq = 0,
): Promise<boolean> {
  for (let attempt = 0; attempt < 10; attempt += 1) {
    const res = await gameApi.getViewerStates(roomId, fromSeq);
    if (!res.success || !res.data || state.roomId !== roomId) {
      return false;
    }

    if (res.data.some((row) => row.sequenceNumber > fromSeq)) {
      adoptLatest(res.data, fromSeq);
      return true;
    }

    await wait(160 + attempt * 80);
  }

  return false;
}

function setGameType(gameType: string): void {
  const nextGameType = gameType.trim() || DEFAULT_GAME_TYPE;
  gameApi = createGameApi(nextGameType);
}

export const yachtMultiplayerActions = {
  initializeAsHost: async (
    roomId: string,
    gameType: string,
    players: InitGameOptions["players"],
    turnMode: YachtTurnModeConfig = "auto",
  ): Promise<void> => {
    setGameType(gameType);
    const myPlayerId = getCurrentUserId() ?? "";
    state = {
      ...INITIAL_STATE,
      roomId,
      gameType,
      myPlayerId,
      isHost: true,
      isObserver: false,
      connection: "connecting",
    };
    notify();

    try {
      const existing = await gameApi.getViewerStates(roomId);
      const existingRows =
        existing.success && existing.data ? existing.data : [];
      if (existingRows.length > 0) {
        adoptLatest(existingRows, 0);
        state = { ...state, connection: "open" };
        notify();
        return;
      }

      await gameApi.submitEvent(roomId, "init", {
        players,
        turnMode,
      });

      await adoptProjectedViewerState(roomId, 0);
      state = { ...state, connection: "open" };
      notify();
    } catch (error) {
      state = { ...state, connection: "closed" };
      setError(error, "ERR_YACHT_INIT_FAILED");
    }
  },

  initializeAsGuest: async (
    roomId: string,
    gameType = DEFAULT_GAME_TYPE,
  ): Promise<void> => {
    setGameType(gameType);
    const myPlayerId = getCurrentUserId() ?? "";
    state = {
      ...INITIAL_STATE,
      roomId,
      gameType,
      myPlayerId,
      isHost: false,
      isObserver: false,
      connection: "connecting",
    };
    notify();

    try {
      const adopted = await adoptProjectedViewerState(roomId, 0);
      if (!adopted && state.roomId === roomId) {
        await gameApi.submitEvent(roomId, "SyncViewerState", {
          actionId: `${roomId}:guest-sync:${myPlayerId}`,
        });
        await adoptProjectedViewerState(roomId, 0);
      }
      state = { ...state, connection: "open" };
      notify();
    } catch (error) {
      state = { ...state, connection: "closed" };
      setError(error, "ERR_YACHT_FETCH_FAILED");
    }
  },

  initializeAsObserver: async (
    roomId: string,
    gameType = DEFAULT_GAME_TYPE,
  ): Promise<void> => {
    setGameType(gameType);
    const myPlayerId = getCurrentUserId() ?? "";
    state = {
      ...INITIAL_STATE,
      roomId,
      gameType,
      myPlayerId,
      isHost: false,
      isObserver: true,
      connection: "connecting",
    };
    notify();

    try {
      const adopted = await adoptProjectedViewerState(roomId, 0);
      if (!adopted && state.roomId === roomId) {
        await gameApi.submitEvent(roomId, "SyncViewerState", {
          actionId: `${roomId}:observer-sync:${myPlayerId}`,
        });
        await adoptProjectedViewerState(roomId, 0);
      }
      state = { ...state, connection: "open" };
      notify();
    } catch (error) {
      state = { ...state, connection: "closed" };
      setError(error, "ERR_YACHT_OBSERVER_FETCH_FAILED");
    }
  },

  replayEvents: async (roomId: string): Promise<void> => {
    try {
      const res = await gameApi.getViewerStates(roomId, state.lastAppliedSeq);
      if (res.success && res.data) {
        adoptLatest(res.data, state.lastAppliedSeq);
        notify();
      }
    } catch (error) {
      setError(error, "ERR_YACHT_REPLAY_FAILED");
    }
  },

  applyRemoteEvent: (
    payloadState: Record<string, unknown>,
    seq: number,
  ): void => {
    if (seq <= state.lastAppliedSeq) return;
    state = {
      ...state,
      viewerState: payloadState as unknown as YachtViewerState,
      lastAppliedSeq: seq,
    };
    notify();
  },

  submitAction: async (
    eventType: string,
    payload: Record<string, unknown> = {},
  ): Promise<void> => {
    const roomId = state.roomId;
    if (!roomId) return;
    if (state.isObserver && eventType !== "SyncViewerState") {
      setError(
        "Observers cannot submit game actions",
        "ERR_OBSERVER_READ_ONLY",
      );
      return;
    }

    try {
      const res = await gameApi.submitEvent(roomId, eventType, payload);
      if (!res.success) {
        setError(res.error ?? "Action rejected", "ERR_YACHT_ACTION");
        return;
      }
      const viewerStates = await gameApi.getViewerStates(
        roomId,
        state.lastAppliedSeq,
      );
      if (
        viewerStates.success &&
        viewerStates.data &&
        state.roomId === roomId
      ) {
        adoptLatest(viewerStates.data, state.lastAppliedSeq);
      }
      if (state.roomId !== roomId) return;
      state = { ...state, lastError: null };
      notify();
    } catch (error) {
      setError(error, "ERR_YACHT_ACTION");
    }
  },

  reset: (): void => {
    state = { ...INITIAL_STATE };
    setGameType(DEFAULT_GAME_TYPE);
    notify();
  },
};

export function useYachtMultiplayerStore(): YachtMultiplayerState {
  return useSyncExternalStore(
    (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    () => state,
  );
}
